// MARGIN APPROVAL on the quote screen (margin-approval, owner 2026-09-23).
//
// ⚠️ THE PANEL IS NOT THE GUARD. The backend refuses a below-limit finalise
// by a Sales user, locks a pending quote against every save, and lets only
// an Admin approve or reject (test_margin_approval.py). This renders what
// the backend reported and offers the actions the signed-in role may take.
//
// ⚠️ EVERY FIGURE IS THE BACKEND'S. The GP % and the limit are printed as
// they arrived and never recalculated here. Nothing on this panel reaches the
// customer document.

import { useState } from "react";
import type { QuoteApproval } from "@/types";
import { formatPercent } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

export interface ApprovalRefusal {
  quoteGpPct: number;
  thresholdPct: number;
}

const BAR: React.CSSProperties = {
  marginTop: spacing[12],
  padding: `${spacing[10]}px ${spacing[12]}px`,
  backgroundColor: semanticColors.warningBg,
  border: `1px solid ${semanticColors.warningBorder}`,
  color: semanticColors.warningTextOnTint,
  borderRadius: radius.base,
  fontSize: typography.fontSize.sm,
  display: "flex", alignItems: "center", gap: spacing[12], flexWrap: "wrap",
};

const BTN: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[12]}px`,
  backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, cursor: "pointer",
};

const PRIMARY: React.CSSProperties = {
  ...BTN, backgroundColor: colors.indigo600, color: semanticColors.onAccent, border: "none",
};

const pct = (v: number | null | undefined) => (typeof v === "number" ? formatPercent(v) : "—");

export function ApprovalPanel({
  approval, refusal, isAdmin, isOwner, busy, error,
  onRequest, onWithdraw, onApprove, onReject,
}: {
  approval?: QuoteApproval;
  /** The last finalise press was refused below the limit. */
  refusal?: ApprovalRefusal;
  isAdmin: boolean;
  isOwner: boolean;
  busy: boolean;
  error: string;
  onRequest: () => void;
  onWithdraw: () => void;
  onApprove: () => void;
  onReject: (reason: string) => void;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const state = approval?.state ?? "none";

  const errorLine = error ? (
    <div data-testid="approval-error" role="alert" style={{ width: "100%", color: semanticColors.destructive }}>
      {error}
    </div>
  ) : null;

  if (state === "pending") {
    return (
      <div data-testid="approval-pending" style={BAR}>
        <span style={{ fontWeight: typography.fontWeight.medium }}>Awaiting approval</span>
        <span data-testid="approval-pending-figures">
          GP {pct(approval?.quoteGpPct)} vs limit {pct(approval?.thresholdPct)}
          {approval?.requestedBy ? ` — requested by ${approval.requestedBy}` : ""}
          . Editing is locked.
        </span>
        {isAdmin && !rejecting && (
          <>
            <button data-testid="approval-approve" disabled={busy} onClick={onApprove} style={PRIMARY}>
              Approve and finalise
            </button>
            <button data-testid="approval-reject" disabled={busy} onClick={() => setRejecting(true)} style={BTN}>
              Reject
            </button>
          </>
        )}
        {isAdmin && rejecting && (
          <>
            <input
              data-testid="approval-reject-reason"
              value={reason}
              placeholder="Reason (required)"
              onChange={(e) => setReason(e.target.value)}
              style={{ ...BTN, cursor: "text", minWidth: 240, color: semanticColors.textPrimary }}
            />
            <button
              data-testid="approval-reject-confirm"
              disabled={busy || !reason.trim()}
              onClick={() => onReject(reason.trim())}
              style={BTN}
            >
              Reject with reason
            </button>
            <button data-testid="approval-reject-cancel" onClick={() => { setRejecting(false); setReason(""); }} style={BTN}>
              Cancel
            </button>
          </>
        )}
        {!isAdmin && isOwner && (
          <button data-testid="approval-withdraw" disabled={busy} onClick={onWithdraw} style={BTN}>
            Withdraw request
          </button>
        )}
        {errorLine}
      </div>
    );
  }

  if (refusal) {
    return (
      <div data-testid="approval-required" style={BAR}>
        <span>
          Below approval limit ({pct(refusal.quoteGpPct)} vs {pct(refusal.thresholdPct)})
        </span>
        <button data-testid="approval-request" disabled={busy} onClick={onRequest} style={PRIMARY}>
          Request approval
        </button>
        {errorLine}
      </div>
    );
  }

  if (state === "rejected") {
    return (
      <div data-testid="approval-rejected" style={BAR}>
        <span style={{ fontWeight: typography.fontWeight.medium }}>Approval rejected</span>
        <span data-testid="approval-rejection-reason">{approval?.rejectionReason}</span>
        {errorLine}
      </div>
    );
  }

  return errorLine;
}
